import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';

export interface RadioButtonProps {
  checked: boolean;
  disabled?: boolean;
  id: string;
  onPress?: () => void;
  labelText: string;
  secondaryText?: string;
  isHovered?: boolean;
  isFocused?: boolean;
  isPressed?: boolean;
}

const RadioButton = ({
  checked = false,
  disabled = false,
  id,
  onPress,
  labelText,
  secondaryText,
  isHovered = false,
  isFocused = false,
  isPressed = false,
}: RadioButtonProps) => {
  const [hovered, setHovered] = useState(isHovered);
  const [focused, setFocused] = useState(isFocused);
  const [pressed, setPressed] = useState(isPressed);

  const handlePress = () => {
    if (!disabled && onPress) {
      onPress();
    }
  };

  const radioCircleStyle = [
    styles.radioCircle,
    checked ? styles.radioCircleChecked : {},
    disabled ? styles.radioCircleDisabled : {},
    !disabled && hovered ? styles.radioCircleHovered : {},
    !disabled && focused ? styles.radioCircleFocused : {},
    !disabled && pressed ? styles.radioCirclePressed : {},
  ];

  const radioInnerCircleStyle = [
    styles.radioInnerCircleChecked,
    disabled ? styles.radioInnerCircleDisabled : {},
    !disabled && hovered ? styles.radioInnerCircleHovered : {},
    !disabled && pressed ? styles.radioInnerCirclePressed : {},
    !disabled && focused ? styles.radioInnerCircleFocused : {},
  ];

  return (
    <Pressable
      id={id}
      style={styles.container}
      onPress={handlePress}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityLabel={labelText}
      accessibilityState={{ checked, disabled }}
    >
      <View style={radioCircleStyle}>
        {checked ? <View style={radioInnerCircleStyle} /> : null}
      </View>
      <View style={styles.textContainer}>
        <BodyBaseText>{labelText}</BodyBaseText>
        {secondaryText ? (
          <BodyXSText style={styles.secondaryText} numberOfLines={1}>
            {secondaryText}
          </BodyXSText>
        ) : null}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.s,
  },
  radioCircle: {
    alignItems: 'center',
    borderColor: theme.color.interactive.primary,
    borderRadius: 24,
    borderWidth: 1,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  radioCircleChecked: {
    borderColor: theme.color.interactive.primary,
  },
  radioCircleDisabled: {
    borderColor: theme.color.interactive.disabled.border,
  },
  radioCircleFocused: {
    borderColor: theme.color.border.focused,
  },
  radioCircleHovered: {
    borderColor: theme.color.interactive.primaryHovered,
  },
  radioCirclePressed: {
    borderColor: theme.color.interactive.primaryPressed,
  },
  radioInnerCircleChecked: {
    backgroundColor: theme.color.interactive.primary,
    borderRadius: 8,
    height: 16,
    width: 16,
  },
  radioInnerCircleDisabled: {
    backgroundColor: theme.color.interactive.disabled.background,
  },
  radioInnerCircleFocused: {
    backgroundColor: theme.color.interactive.primary,
  },
  radioInnerCircleHovered: {
    backgroundColor: theme.color.interactive.primaryHovered,
  },
  radioInnerCirclePressed: {
    backgroundColor: theme.color.interactive.primaryPressed,
  },
  secondaryText: {
    color: theme.color.text.secondary,
  },
  textContainer: {
    flex: 1,
  },
});

export default RadioButton;
